import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Mail, CheckCircle2, AlertTriangle, ShieldCheck } from "lucide-react";
import { EASE } from "@/components/Reveal";
import { scrollToSection } from "@/components/Nav";

const HEADLINE = ["US Patent Data", "Validation & Deadline", "Verification"];

const RECORDS = [
  { app: "18/342,117", pat: "11,982,441", status: "VERIFIED", ok: true },
  { app: "17/559,802", pat: "12,104,990", status: "VERIFIED", ok: true },
  { app: "16/874,335", pat: "11,704,221", status: "DISCREPANCY", ok: false },
  { app: "18/120,564", pat: "—", status: "VERIFIED", ok: true },
  { app: "17/206,918", pat: "11,887,552", status: "REVIEW", ok: false },
];

function LedgerCard({ className, style }) {
  return (
    <motion.div style={style} className={`bg-white/[0.04] border border-white/10 backdrop-blur-md p-5 ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <span className="font-monodata text-[10px] tracking-[0.2em] text-slate-400 uppercase">Portfolio Validation Ledger</span>
        <ShieldCheck className="w-4 h-4 text-blue-400" />
      </div>
      <div className="space-y-2.5">
        {RECORDS.map((r, i) => (
          <motion.div
            key={r.app}
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 1.1 + i * 0.14, duration: 0.7, ease: EASE }}
            className="flex items-center justify-between gap-4 font-monodata text-[11px] sm:text-xs"
          >
            <span className="text-slate-300">{r.app}</span>
            <span className="text-slate-500 hidden sm:inline">{r.pat}</span>
            <span className={`inline-flex items-center gap-1.5 ${r.ok ? "text-emerald-400" : "text-amber-400"}`}>
              {r.ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
              {r.status}
            </span>
          </motion.div>
        ))}
      </div>
      <div className="mt-4 pt-3 border-t border-white/10 flex justify-between font-monodata text-[10px] text-slate-500">
        <span>5 RECORDS CHECKED</span>
        <span>3 VERIFIED · 2 FLAGGED</span>
      </div>
    </motion.div>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yBack = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const yFront = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  return (
    <section id="home" ref={ref} data-testid="hero-section" className="relative bg-navy grain overflow-hidden min-h-screen flex items-center">
      <motion.div style={{ y: bgY }} className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1644088379091-d574269d422f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGRhcmslMjBibHVlJTIwZ3JpZCUyMGRhdGElMjB2aXN1YWxpemF0aW9ufGVufDB8fHx8MTc4MjQ0MDkyOXww&ixlib=rb-4.1.0&q=85"
          alt=""
          className="w-full h-full object-cover opacity-25"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-navy/70 via-navy/80 to-navy" />
      </motion.div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pt-36 pb-24 lg:pt-44 lg:pb-32 grid lg:grid-cols-12 gap-16 items-center w-full">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2, ease: EASE }}
            className="font-monodata text-xs tracking-[0.3em] uppercase text-blue-400 mb-8"
            data-testid="hero-eyebrow"
          >
            Specialized IP Data Validation Support
          </motion.p>

          <h1 className="font-display font-black text-white text-4xl sm:text-5xl lg:text-[4.35rem] leading-[0.98] tracking-tighter" data-testid="hero-headline">
            {HEADLINE.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  initial={{ y: "112%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 1.1, delay: 0.35 + i * 0.14, ease: EASE }}
                >
                  {i === 2 ? <span className="text-blue-400">{line}</span> : line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.9, ease: EASE }}
            className="mt-8 text-base md:text-lg text-slate-300 leading-relaxed max-w-xl"
            data-testid="hero-subheadline"
          >
            Improve the accuracy of your patent records with professional IP data validation,
            patent information verification, and deadline checking services designed for law
            firms and corporate IP teams.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.05, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-consultation"
              onClick={() => scrollToSection("#contact")}
              className="inline-flex items-center gap-2 bg-accent text-white font-semibold px-7 py-4 text-sm hover:bg-blue-500 transition-colors duration-300 group"
            >
              Request a Consultation
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button
              data-testid="hero-cta-contact"
              onClick={() => scrollToSection("#contact")}
              className="inline-flex items-center gap-2 border border-white/25 text-white font-semibold px-7 py-4 text-sm hover:bg-white/10 hover:border-white/40 transition-colors duration-300"
            >
              Contact Us
            </button>
          </motion.div>

          <motion.a
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.9, delay: 1.25 }}
            href="mailto:info@certifyipglobal.com"
            data-testid="hero-email-link"
            className="mt-8 inline-flex items-center gap-2.5 text-sm text-slate-300 hover:text-white transition-colors duration-300 group"
          >
            <Mail className="w-4 h-4 text-blue-400" />
            <span className="font-monodata border-b border-transparent group-hover:border-blue-400 transition-colors duration-300">
              info@certifyipglobal.com
            </span>
          </motion.a>
        </div>

        <div className="lg:col-span-5 relative hidden lg:block" style={{ perspective: 1200 }}>
          <motion.div
            initial={{ opacity: 0, rotateY: -18, y: 40 }}
            animate={{ opacity: 1, rotateY: -8, y: 0 }}
            transition={{ duration: 1.3, delay: 0.8, ease: EASE }}
          >
            <LedgerCard style={{ y: yFront }} className="relative z-10 shadow-2xl" />
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.2, delay: 1.3 }}
            style={{ y: yBack }}
            className="absolute -bottom-10 -right-6 w-3/4 bg-navy-light/60 border border-white/10 backdrop-blur-sm p-5 -z-0"
          >
            <div className="font-monodata text-[10px] tracking-[0.2em] text-slate-500 uppercase mb-3">Deadline Check · US Prosecution</div>
            <div className="space-y-2">
              {["3.5Y MAINTENANCE — CONFIRMED", "RESPONSE DUE — FLAGGED FOR REVIEW", "ISSUE FEE — VERIFIED"].map((t) => (
                <div key={t} className="font-monodata text-[10px] text-slate-400 flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-blue-400 inline-block" /> {t}
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-5 flex flex-wrap gap-x-10 gap-y-2 justify-between font-monodata text-[10px] tracking-[0.25em] uppercase text-slate-500">
          <span data-testid="hero-trust-1">Structured. Accurate. Scalable.</span>
          <span data-testid="hero-trust-2" className="hidden sm:inline">Designed for Law Firms &amp; Corporate IP Teams</span>
          <span data-testid="hero-trust-3" className="hidden md:inline">US Patent Data Specialists</span>
        </div>
      </div>
    </section>
  );
}
